import React, { useState, useMemo, useEffect } from "react";
import { Button, Label } from "reactstrap";
import SearchableSelect, { SearchableSelectOption } from "../../../Components/Common/SearchableSelect";
import { showConfirmModal, hideModal, hideAllModals } from "../../../hooks/useModal";
import { toastSuccess, toastError } from "../../../utils/toast";
import { AlerteDetail } from "../AlerteDetail/types";
import { 
  useEquipesQuery, 
  useChefEquipeQuery, 
  useAffectationEquipeMutation,
  useAffectationEquipeMultipleMutation 
} from "../../../hooks/queries/useRoadAlertQueries";
import { useAuthStore } from "../../../stores/useAuthStore";

interface AffectationEquipeModalProps {
  alerte?: AlerteDetail;
  alertes?: AlerteDetail[];
  onSuccess: () => void;
}

const AffectationEquipeModal: React.FC<AffectationEquipeModalProps> = ({ alerte, alertes = [], onSuccess }) => {
  const [idEquipe, setIdEquipe] = useState<string | number | null>(null);
  const [idChefEquipe, setIdChefEquipe] = useState<string | number | null>(null);

  const targetAlertes = useMemo(() => {
    if (alerte) return [alerte];
    return alertes;
  }, [alerte, alertes]);

  const isMultiple = targetAlertes.length > 1;

  const user = useAuthStore((s) => s.user);
  const usernameModif = user?.username || "";

  const equipesQuery = useEquipesQuery();
  const { data: chefEquipeData, isLoading: isLoadingChefEquipe } = useChefEquipeQuery(idEquipe);
  
  const affectationMutation = useAffectationEquipeMutation();
  const affectationMultipleMutation = useAffectationEquipeMultipleMutation();

  useEffect(() => {
    setIdChefEquipe(null);
  }, [idEquipe]);

  const equipeOptions: SearchableSelectOption[] = useMemo(() => {
    const data = (equipesQuery.data ?? []) as any[];
    return [
      { value: "", label: "Sélectionner une équipe" },
      ...data.map((e) => ({
        value: String(e.idEquipe),
        label: e.nomEquipe,
      })).filter((o) => o.value !== ""),
    ];
  }, [equipesQuery.data]);

  const chefOptions: SearchableSelectOption[] = useMemo(() => {
    const data = (chefEquipeData ?? []) as any[];
    return [
      { value: "", label: "Sélectionner un chef d'équipe" },
      ...data.map((c) => ({
        value: String(c.idUsers),
        label: c.name,
      })).filter((o) => o.value !== ""),
    ];
  }, [chefEquipeData]);

  const selectedChef = useMemo(() => {
    const data = (chefEquipeData ?? []) as any[];
    return data.find((c: any) => String(c.idUsers) === String(idChefEquipe));
  }, [chefEquipeData, idChefEquipe]);

  const selectedEquipeLabel = useMemo(
    () => equipeOptions.find((o) => String(o.value) === String(idEquipe))?.label ?? "",
    [equipeOptions, idEquipe]
  );
  const selectedChefLabel = useMemo(
    () => chefOptions.find((o) => String(o.value) === String(idChefEquipe))?.label ?? "",
    [chefOptions, idChefEquipe]
  );

  const submitting = affectationMutation.isPending || affectationMultipleMutation.isPending;
  const canSubmit = idEquipe && idEquipe !== "" && idChefEquipe && idChefEquipe !== "" && !submitting && targetAlertes.length > 0;

  const handleValider = () => {
    if (!canSubmit) return;
    const chefUsername = (selectedChef as any)?.username ?? (selectedChef as any)?.name ?? "";

    const confirmMessage = isMultiple
      ? `Voulez-vous affecter ces ${targetAlertes.length} alertes à l'équipe « ${selectedEquipeLabel} » et au chef d'équipe « ${selectedChefLabel} » ?`
      : `Voulez-vous affecter cette alerte à l'équipe « ${selectedEquipeLabel} » et au chef d'équipe « ${selectedChefLabel} » ?`;

    showConfirmModal({
      title: "Confirmer l'affectation",
      message: confirmMessage,
      confirmText: "Confirmer",
      cancelText: "Annuler",
      confirmVariant: "primary",
      type: "info",
      onConfirm: () => {
        if (!usernameModif) {
          toastError("Nom d'utilisateur non disponible. Veuillez vous reconnecter.");
          return;
        }
        if (!chefUsername) {
          toastError("Impossible d'identifier le chef d'équipe (username manquant).");
          return;
        }

        if (isMultiple) {
          const payload = {
            idEquipe: String(idEquipe),
            idChefEquipe: String(idChefEquipe),
            idStatutAlerte: "2",
            idPriorite: "2", // Valeur par défaut pour l'affectation multiple
            username: chefUsername,
            usernameModif,
            affecteAlerte: targetAlertes.map(a => ({ idAlerte: a.idAlerte }))
          };

          affectationMultipleMutation.mutate(payload, {
            onSuccess: () => {
              toastSuccess(`${targetAlertes.length} alertes ont été affectées avec succès.`);
              hideAllModals();
              onSuccess();
            },
            onError: (err: any) => {
              toastError(err?.message || "Erreur lors de l'affectation multiple.");
            }
          });
        } else {
          const firstAlerte = targetAlertes[0];
          const payload = {
            idAlerte: firstAlerte.idAlerte,
            idStatutAlerte: 2,
            idPriorite: firstAlerte.priorite?.idPriorite,
            idTypeChausse: firstAlerte.typeChausse?.idTypeChausse,
            nomVoie: firstAlerte.nomVoie ?? "",
            usernameModif,
            username: chefUsername,
            idEquipe: String(idEquipe),
            idChefEquipe: String(idChefEquipe),
          };
          affectationMutation.mutate(payload, {
            onSuccess: () => {
              toastSuccess("L'alerte a été affectée avec succès.");
              hideAllModals();
              onSuccess();
            },
            onError: (err: any) => {
              toastError(err?.message || "Erreur lors de l'affectation.");
            },
          });
        }
      },
    });
  };

  return (
    <div className="p-2">
      <div className="p-3 bg-light rounded-3 mb-4 border">
        {isMultiple ? (
          <div className="d-flex align-items-center">
            <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: 40, height: 40 }}>
              <i className="ri-notification-3-line fs-5"></i>
            </div>
            <div>
              <h6 className="mb-0 fw-bold">Affectation multiple</h6>
              <p className="text-muted small mb-0">{targetAlertes.length} alertes sélectionnées</p>
            </div>
          </div>
        ) : (
          <div>
            <Label className="text-uppercase text-muted small fw-bold mb-1">Alerte sélectionnée</Label>
            <h6 className="mb-0 text-truncate">
              {targetAlertes[0]?.libelleAlerte ?? targetAlertes[0]?.description ?? `#${targetAlertes[0]?.idAlerte}`}
            </h6>
          </div>
        )}
      </div>

      <div className="mb-3">
        <Label className="fw-medium">Équipe de destination</Label>
        <SearchableSelect
          options={equipeOptions}
          value={idEquipe}
          onChange={(v) => setIdEquipe(v)}
          placeholder="Sélectionner une équipe"
          isDisabled={equipesQuery.isLoading}
        />
      </div>
      <div className="mb-4">
        <Label className="fw-medium">Chef d'équipe responsable</Label>
        <SearchableSelect
          options={chefOptions}
          value={idChefEquipe}
          onChange={(v) => setIdChefEquipe(v)}
          placeholder="Sélectionner un chef d'équipe"
          isDisabled={isLoadingChefEquipe || !idEquipe || idEquipe === ""}
          isLoading={isLoadingChefEquipe}
        />
      </div>
      <div className="d-flex justify-content-end gap-2 mt-3 pt-3 border-top">
        <Button color="light" onClick={() => hideModal()} disabled={submitting}>
          Annuler
        </Button>
        <Button 
          color="primary" 
          className="px-4"
          disabled={!canSubmit} 
          onClick={handleValider}
        >
          {submitting ? (
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          ) : null}
          {submitting ? "Affectation..." : "Confirmer l'affectation"}
        </Button>
      </div>
    </div>
  );
};

export default AffectationEquipeModal;
